import React, { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';

// Keep the field readable while leaving room for the keyboard and submit button.
const AutoTextarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function AutoTextarea({ onInput, style, ...props }, forwardedRef) {
  const node = useRef<HTMLTextAreaElement | null>(null);
  const resize = useCallback(() => {
    const el = node.current;
    if (!el || !el.clientWidth) return;
    const css = getComputedStyle(el);
    const viewport = window.visualViewport?.height || window.innerHeight;
    const min = Math.min(parseFloat(css.minHeight) || 44, Math.max(80, viewport * .35));
    const max = Math.max(min, Math.min(320, viewport * .35));
    el.style.height = 'auto';
    const height = Math.max(min, Math.min(max, el.scrollHeight + 2));
    el.style.height = `${height}px`;
    el.style.overflowY = el.scrollHeight > height ? 'auto' : 'hidden';
  }, []);
  useLayoutEffect(resize, [props.value, resize]);
  useLayoutEffect(() => {
    const el = node.current;
    if (!el) return;
    let width = -1;
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (el.clientWidth !== width) { width = el.clientWidth; resize(); }
    });
    observer?.observe(el);
    window.visualViewport?.addEventListener('resize', resize);
    window.addEventListener('resize', resize);
    return () => { observer?.disconnect(); window.visualViewport?.removeEventListener('resize', resize); window.removeEventListener('resize', resize); };
  }, [resize]);
  return <textarea {...props} ref={el => { node.current = el; if (typeof forwardedRef === 'function') forwardedRef(el); else if (forwardedRef) forwardedRef.current = el; }} style={{ ...style, resize: 'none', boxSizing: 'border-box', maxHeight: 'min(320px, calc(var(--visual-height, 100dvh) * .35))' }} onInput={e => { resize(); onInput?.(e); }} />;
});
export default AutoTextarea;
